import {
    BidProfileStatus,
    ScheduleSource,
    QualificationRule,
    SortOrderType,
    DataviewFieldDataType,
    EmployeeBiddingStatus,
    BidProcessType,
    TimeUnit,
    EmployeeBidProfileColumn
} from '..';
import i18n from '../../services/i18n';

/**
 * @param {BidProcessType} processType
 */
export const getBidProcessTypeLabel = (processType) => {
    switch (processType) {
        case BidProcessType.Preferential:
            return i18n.t('shiftBidding:common.bidProcessType.preferential');
        case BidProcessType.Sequential:
            return i18n.t('shiftBidding:common.bidProcessType.sequential');
        default:
            return '';
    }
};

/**
 * @param {ScheduleSource} scheduleSourceId
 */
export const getScheduleSourceLabel = (scheduleSourceId) => {
    switch (scheduleSourceId) {
        case ScheduleSource.WorkplanManager:
            return i18n.t('shiftBidding:common.scheduleSource.workplan');
        case ScheduleSource.ExcelFile:
            return i18n.t('shiftBidding:common.scheduleSource.excel');
        default:
            return '';
    }
};

export const getAutoApprovalLabel = (/** @type {boolean} */isAutoApproval) => (
    isAutoApproval
        ? i18n.t('shiftBidding:common.autoApproval.auto')
        : i18n.t('shiftBidding:common.autoApproval.manual')
);

/**
 * @param {QualificationRule} qualificationRuleId
 * @param {boolean} isUseUkgReadyTerminology
 */
export const getQualificationRuleLabel = (qualificationRuleId, isUseUkgReadyTerminology = false) => {
    const suffix = isUseUkgReadyTerminology ? '_ukgReady' : '';
    switch (qualificationRuleId) {
        case QualificationRule.None:
            return i18n.t('shiftBidding:common.qualificationRule.none');
        case QualificationRule.MatchQualification:
            return i18n.t('shiftBidding:common.qualificationRule.matchQualification');
        case QualificationRule.MatchPrimaryJobAndJobTransferSet:
            return i18n.t(`shiftBidding:common.qualificationRule.matchPrimaryJobAndJobTransferSet${suffix}`);
        case QualificationRule.MatchPrimaryJobOnly:
            return i18n.t(`shiftBidding:common.qualificationRule.matchPrimaryJobOnly${suffix}`);
        default:
            return '';
    }
};

/**
 * @param {BidProfileStatus|undefined} statusId
 * @returns {string}
 */
export const getProfileStatusLabel = (statusId) => {
    switch (statusId) {
        case BidProfileStatus.Draft:
            return i18n.t('shiftBidding:common.bidProfileStatus.draft');
        case BidProfileStatus.Scheduled:
            return i18n.t('shiftBidding:common.bidProfileStatus.scheduled');
        case BidProfileStatus.Active:
            return i18n.t('shiftBidding:common.bidProfileStatus.active');
        case BidProfileStatus.Closed:
            return i18n.t('shiftBidding:common.bidProfileStatus.closed');
        case BidProfileStatus.Published:
            return i18n.t('shiftBidding:common.bidProfileStatus.published');
        case BidProfileStatus.Canceled:
            return i18n.t('shiftBidding:common.bidProfileStatus.canceled');
        default:
            return '';
    }
};

/**
 * @param {SortOrderType} sortOrderType
 * @returns {string}
 */
export const getSortOrderTypeLabel = (sortOrderType) => {
    switch (sortOrderType) {
        case SortOrderType.Ascending:
            return i18n.t('shiftBidding:common.sortType.ascending');
        case SortOrderType.Descending:
            return i18n.t('shiftBidding:common.sortType.descending');
        default:
            return '';
    }
};

/**
 * @param {DataviewFieldDataType} dataviewFieldDataType
 * @returns {string}
 */
export const getDataTypeLabel = (dataviewFieldDataType) => {
    switch (dataviewFieldDataType) {
        case DataviewFieldDataType.Numeric:
            return i18n.t('shiftBidding:common.dataType.number');
        case DataviewFieldDataType.Date:
            return i18n.t('shiftBidding:common.dataType.date');
        default:
            return '';
    }
};

/**
 * @param {TimeUnit} timeUnit
 * @returns {string}
 */
export const getTimeUnitLabel = (timeUnit) => {
    switch (timeUnit) {
        case TimeUnit.Hour:
            return i18n.t('shiftBidding:common.timeUnit.hour_default');
        case TimeUnit.Day:
            return i18n.t('shiftBidding:common.timeUnit.day_default');
        default:
            return '';
    }
};

/**
 * @param {BidProfileManagement.EmployeeSubmissionStatus} statusId
 * @returns {string}
 */
export const getEmployeeSubmissionStatusLabel = (statusId) => {
    switch (statusId) {
        case EmployeeBiddingStatus.Unsubmitted:
            return i18n.t('shiftBidding:common.employeeBidStatus.unsubmitted');
        case EmployeeBiddingStatus.Submitted:
            return i18n.t('shiftBidding:common.employeeBidStatus.submitted');
        case EmployeeBiddingStatus.Unawarded:
            return i18n.t('shiftBidding:common.employeeBidStatus.unawarded');
        case EmployeeBiddingStatus.Approved:
            return i18n.t('shiftBidding:common.employeeBidStatus.approved');
        case EmployeeBiddingStatus.Refused:
            return i18n.t('shiftBidding:common.employeeBidStatus.refused');
        case EmployeeBiddingStatus.Declined:
            return i18n.t('shiftBidding:common.employeeBidStatus.declined');
        default:
            return '';
    }
};

/**
 * @param {DayOfWeek} anchorDay
 */
export const getDayOfTheWeekLabel = (anchorDay) => i18n.t(`shiftBidding:common.dayOfWeek.${anchorDay}`);

/**
 * @param {boolean} isEnabled
 */
export const getEnabledDisabledLabel = (isEnabled) => (
    isEnabled ? i18n.t('shiftBidding:common.enabled') : i18n.t('shiftBidding:common.disabled')
);

/**
 * @param {WorkplanPublishStepCode} stepCode
 */
export const getWorkplanPublishStepCodeLabel = (stepCode) =>
    i18n.t(`shiftBidding:manager.bidProfileEdit.bidDashboard.workplanStepNames.${stepCode}`);

/**
 * @param {AwardEmployeesSteps} stepCode
 */
export const getAwardingStepCodeLabel = (stepCode) =>
    i18n.t(`shiftBidding:manager.bidProfileEdit.bidDashboard.awardingStepNames.${stepCode}`);

/**
 * @param {ImportScheduleSteps} stepCode
 */
export const getImportScheduleStepsLabel = (stepCode) =>
    i18n.t(`shiftBidding:manager.bidProfileEdit.bidDashboard.importScheduleStepNames.${stepCode}`);

/**
 * @param {PublishStatusType} publishStatusType
 */
export const getSummaryPublishStatusTypeHeaderLabel = (publishStatusType) =>
    i18n.t(`shiftBidding:manager.bidProfileEdit.bidDashboard.publishProgress.summary.statusHeader.${publishStatusType}`);

/**
 * @param {PublishStatusType} publishStatusType
 */
export const getSummaryPublishStatusTypeDescriptionLabel = (publishStatusType) =>
    i18n.t(`shiftBidding:manager.bidProfileEdit.bidDashboard.publishProgress.summary.statusDescription.${publishStatusType}`);

/**
 * @param {PublishStatusType} publishStatusType
 */
export const getStepPublishStatusTypeLabel = (publishStatusType) =>
    i18n.t(`shiftBidding:manager.bidProfileEdit.bidDashboard.publishProgress.steps.statusLabel.${publishStatusType}`);

/**
 * @param {EmployeeBidProfileColumn} column
 * @returns {string}
 */
export const getEmployeeBidProfileColumnLabel = (column) => {
    switch (column) {
        case EmployeeBidProfileColumn.Available:
            return i18n.t('shiftBidding:employee.dashboard.columns.available');
        case EmployeeBidProfileColumn.Draft:
            return i18n.t('shiftBidding:employee.dashboard.columns.draft');
        case EmployeeBidProfileColumn.Submitted:
            return i18n.t('shiftBidding:employee.dashboard.columns.submitted');
        case EmployeeBidProfileColumn.Closed:
            return i18n.t('shiftBidding:employee.dashboard.columns.closed');
        default:
            return '';
    }
};
